import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Building2, Plus, Search } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { ClientTable } from '../../components/clients/ClientTable';
import { ClientFormModal } from '../../components/modals/ClientFormModal';
import { ClientDetailsModal } from '../../components/modals/ClientDetailsModal';
import { StatsCardGrid } from '../../components/dashboard/StatsCardGrid';
import { useClients } from '../../hooks/useClients';
import { Client, ClientFormData, DashboardData } from '../../types';
import { adminAPI } from '../../services/api/adminAPI';
import { CustomSelect } from '../../components/common/CustomSelect';

interface OutletContext {
	dashboardData: DashboardData | null;
	refreshKey: number;
}

export const ClientOverviewPage: React.FC = () => {
	const { dashboardData, refreshKey } = useOutletContext<OutletContext>();
	const { isAuthenticated } = useAuth();
	const { isClientUser, isAdmin, canCreateClient, clientName } = usePermissions();
	const { clients, loading, loadClients, createClient, updateClient } = useClients();

	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('All Status');
	const [showClientModal, setShowClientModal] = useState(false);
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	const [editingClient, setEditingClient] = useState<Client | null>(null);
	const [viewingClient, setViewingClient] = useState<Client | null>(null);
	const [modalLoading, setModalLoading] = useState(false);

	useEffect(() => {
		if (refreshKey > 0) {
			loadClients({});
		}
	}, [refreshKey]);

	useEffect(() => {
		const loadOptions: {
			search?: string;
			status?: string;
			healthStatus?: string;
		} = {};

		if (searchTerm) {
			loadOptions.search = searchTerm;
		}

		if (statusFilter !== 'All Status') {
			loadOptions.status = statusFilter;
		}

		loadClients(loadOptions);
	}, [searchTerm, statusFilter]);

	// Close all modals when user authentication changes (e.g., logout)
	useEffect(() => {
		if (!isAuthenticated) {
			setShowClientModal(false);
			setShowDetailsModal(false);
			setEditingClient(null);
			setViewingClient(null);
		}
	}, [isAuthenticated]);

	const handleAddClient = () => {
		setEditingClient(null);
		setShowClientModal(true);
	};

	const handleEditClient = async (client: Client) => {
		try {
			setModalLoading(true);
			// Fetch detailed client information for editing
			const detailedClient = await adminAPI.getClientDetails(client.id);

			// Use detailedClient directly since it now has complete data including config
			const mergedClient = detailedClient;

			setEditingClient(mergedClient);
			setShowClientModal(true);
		} catch (error) {
			console.error('Error fetching client details for edit:', error);
			// Fallback to basic client data if detailed fetch fails
			setEditingClient(client);
			setShowClientModal(true);
		} finally {
			setModalLoading(false);
		}
	};

	const handleViewClient = async (client: Client) => {
		try {
			// Fetch detailed client information
			const detailedClient = await adminAPI.getClientDetails(client.id);

			// Merge the original client data with detailed data to preserve monthly stats
			// Use detailedClient as base since it has complete config data, then overlay with correct values
			const mergedClient = {
				...detailedClient, // Start with detailed client data (has config)
				// Preserve monthly volume and sync stats from the original client list
				monthlyVolume: client.monthlyVolume,
				syncStats: client.syncStats,
				// Preserve correct integration status from original client data
				integrations: client.integrations,
			};

			setViewingClient(mergedClient);
			setShowDetailsModal(true);
		} catch (error) {
			console.error('Failed to fetch client details:', error);
			// Fallback to basic client data
			setViewingClient(client);
			setShowDetailsModal(true);
		}
	};

	const handleSubmitClient = async (formData: ClientFormData) => {
		setModalLoading(true);
		try {
			if (editingClient) {
				await updateClient(editingClient.id, formData);
			} else {
				await createClient(formData);
			}
			setShowClientModal(false);
			setEditingClient(null);
		} catch (error) {
			// Error handling is done in the hook
		} finally {
			setModalLoading(false);
		}
	};

	return (
		<div className="space-y-6">
			{/* Client Context Banner for CLIENT_USER */}
			{isClientUser && clientName && (
				<div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
					<div className="flex items-center space-x-3">
						<div className="flex-shrink-0">
							<div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
								<Building2 className="h-6 w-6 text-white" />
							</div>
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold text-gray-900">{clientName}</h3>
							<p className="text-sm text-gray-600">
								Monitor your integration health, sync activity, and manage your account settings
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Platform Stats - Only visible to ADMIN users */}
			{isAdmin && dashboardData && <StatsCardGrid data={dashboardData} />}

			<div className="bg-white rounded-xl border border-gray-200">
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-semibold text-gray-900">{isAdmin ? 'All Clients' : 'Client Overview'}</h3>
						<div className="flex items-center space-x-4">
							{/* Search - only show for admins */}
							{isAdmin && (
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
									<input
										type="text"
										placeholder="Search clients..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
									/>
								</div>
							)}

							{/* Status Filter - only show for admins */}
							{isAdmin && (
								<CustomSelect
									value={statusFilter}
									onChange={(value) => setStatusFilter(value)}
									options={[
										{ value: 'All Status', label: 'All Status' },
										{ value: 'ACTIVE', label: 'ACTIVE' },
										{ value: 'INACTIVE', label: 'INACTIVE' },
									]}
								/>
							)}

							{/* Add Client Button - only show for admins */}
							{canCreateClient && (
								<button
									onClick={handleAddClient}
									className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center text-sm font-medium">
									<Plus className="h-4 w-4 mr-2" />
									Add Client
								</button>
							)}
						</div>
					</div>
				</div>

				<ClientTable
					clients={clients}
					loading={loading}
					onEdit={handleEditClient}
					onView={handleViewClient}
					onAdd={handleAddClient}
				/>
			</div>

			<ClientFormModal
				isOpen={showClientModal}
				onClose={() => setShowClientModal(false)}
				onSubmit={handleSubmitClient}
				client={editingClient}
				loading={modalLoading}
			/>

			<ClientDetailsModal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} client={viewingClient} />
		</div>
	);
};
